/**
 * Public entry for `@morfi/backend-sdk-v2/platform/web`.
 *
 * Surfaces the web-specific helpers consumers need to bootstrap the
 * SDK: a zero-config `createWebDefaultAdapter()`, the lower-level
 * `createWebPlatformAdapter()` for advanced wiring, and the
 * `IntersectionObserver`-backed visibility install.
 */

// Signing runtime — relocated from frontend/src/lib/wallet/signing.ts.
export {
  resolveSigningMethod,
  type SigningWallet,
  signMessage,
  signTransaction,
} from '../../auth/turnkey/signing.js';
export type {
  ExternalWalletProvider,
  SigningDeps,
  TurnkeySignMessageFn,
  TurnkeySignTransactionFn,
} from '../../auth/turnkey/signing-types.js';
// Market-creation orchestrator + React hook. Platform-neutral by import
// (only React + @solana/web3.js + @morfi/contract-sdk). RN consumes the
// same module via the parallel platform/react-native barrel.
export {
  type AddOutcomeParams,
  type AddOutcomeProgressState,
  type AddOutcomeResult,
  type CreateMarketParams,
  type CreateMarketProgressState,
  type CreateMarketResult,
  INITIAL_ADD_OUTCOME_PROGRESS_STATE,
  INITIAL_PROGRESS_STATE as MARKET_CREATION_INITIAL_PROGRESS,
  type MarketEditorialCreateVars,
  runAddOutcome,
  runMarketCreation,
  type UseAddOutcomeApi,
  type UseAddOutcomeDeps,
  type UseCreateMarketApi,
  type UseCreateMarketDeps,
  useAddOutcome,
  useCreateMarket,
} from '../../market-creation/index.js';
export {
  type ActiveWallet,
  type BuildResult,
  type CapStatus,
  type CapUtilization,
  type ConfirmationResult,
  clearRelayFeePayerCache,
  confirmTransaction,
  createInertTurnkeyDeps,
  createTransactionTraceId,
  createTxError,
  DEFAULT_LANDING_CONFIG,
  getRecoveryAction,
  hashDiagnosticEntity,
  isAmbiguousLandingError,
  type JitoConfig,
  type LandingConfig,
  MAX_PACKET_BYTES,
  measureNormalizedTransactionPacket,
  type NormalizedTransactionPacketMeasurement,
  type PreflightResult,
  type RecoveryAction,
  type RelayErrorResponse,
  type RelayResponse,
  resolveRelayFeePayer,
  type SimulationResult,
  SPL_TOKEN_ERROR_MESSAGES,
  SPL_TOKEN_ERROR_NAMES,
  type SubmissionOptions,
  type SubmissionResult,
  safeTxErrorCode,
  type TGetGasUsageBody,
  type TGetGasUsageResponse,
  type TGetSendTransactionStatusBody,
  type TGetSendTransactionStatusResponse,
  type TurnkeyDeps,
  TX_ERROR_MESSAGES,
  type TxConnectionConfig,
  type TxEngineConfig,
  type TxEngineEmitEvent,
  type TxEngineFlags,
  type TxEngineLogger,
  TxError,
  TxErrorCode,
  type TxErrorCodeType,
  type TxErrorInfo,
  type TxOnForeground,
  type TxPath,
  type TxStage,
  type WalletType,
  type WsHealthState,
} from '../../tx-engine/index.js';
// Impersonation subsystem — web platform (spec 009 FILE 14 Group F).
export { GrantApiClientError } from './auth/impersonation/impersonation-api-client.js';
export {
  attachImpersonationHeader,
  IMPERSONATION_HEADER_NAME,
  type ImpersonationStateSnapshot,
} from './auth/impersonation/impersonation-header.js';
export {
  type TeardownCallbacks,
  type TeardownState,
  teardownImpersonation,
} from './auth/impersonation/impersonation-teardown.js';
export {
  type ImpersonationStoreMutators,
  type ImpersonationStoreSlice,
  SdkImpersonationProvider,
  type SdkImpersonationProviderProps,
} from './auth/impersonation/sdk-impersonation-provider.js';
export {
  type ActivateInput,
  type UseImpersonationResult,
  useImpersonation,
} from './auth/impersonation/use-impersonation.js';
// Web-only OAuth connect helpers — relocated from the frontend (auth group D).
export {
  type OauthConnectProvider,
  OauthConnectStash,
  type OauthConnectStashEntry,
} from './auth/oauth/oauth-connect-stash.js';
export {
  type ConnectProviderArgs,
  type GoogleConnectResult,
  type GoogleConnectUser,
  type UseGoogleConnectDeps,
  useGoogleConnect,
} from './auth/oauth/use-google-connect.js';
export {
  isPopupBlockedError,
  type OauthConnectResult,
  type RunOauthConnectArgs,
  useOauthConnect,
} from './auth/oauth/use-oauth-connect.js';
export { startXWaitlistJoin, stripXWaitlistQueryParams } from './auth/oauth/x-waitlist.js';
// Auth orchestration — relocated from the frontend (spec 009 FILE 14 Group B).
export {
  SdkAuthContext,
  type SdkAuthContextValue,
  SdkAuthProvider,
  type SdkAuthProviderCallbacks,
  type SdkAuthProviderProps,
  type SdkAuthState,
  useSdkAuthContext,
} from './auth/session/auth-provider.js';
export {
  type AuthSuccessToastMessages,
  SdkAuthSuccessHandler,
  type SdkAuthSuccessHandlerCallbacks,
  type SdkAuthSuccessHandlerProps,
} from './auth/session/auth-success-handler.js';
export {
  SdkNetworkAuthBridge,
  type SdkNetworkAuthBridgeProps,
} from './auth/session/network-auth-bridge.js';
export {
  findWalletsInAnotherSubOrg,
  isWalletInAnotherSubOrg,
} from './auth/session/reconcile-helpers.js';
export {
  type CreationWalletProof,
  type CreatorSigningSession,
  type CreatorSigningTarget,
  createCreatorSigningSession,
} from './auth/turnkey/creator-signing-session.js';
// Web Turnkey integration — SDK-owned auth vendor binding (auth cutover).
export {
  type SdkTurnkeyWebConfig,
  SdkTurnkeyWebProvider,
  type SdkTurnkeyWebProviderProps,
} from './auth/turnkey/turnkey-provider.js';
export {
  type AdminMarketDraft,
  useAdminMarketCreation,
} from './auth/turnkey/use-admin-market-creation.js';
export { type CreatorLoginChallenge, useCreatorLogin } from './auth/turnkey/use-creator-login.js';
// SDK-owned Turnkey wrapper — exposes useTurnkey()+useModal() under the SDK
// boundary so frontend never imports @turnkey/react-wallet-kit directly.
export {
  SdkKeyFormat,
  SdkOtpType,
  type SdkTurnkeyContext,
  useSdkTurnkey,
} from './auth/turnkey/use-sdk-turnkey.js';
// Operator-facing Turnkey gas-sponsorship usage hook — relocated from the
// frontend (auth group E — submission/gas moves into the SDK).
export {
  TURNKEY_GAS_USAGE_QUERY_KEY,
  type TurnkeyGasUsage,
  type TurnkeyGasUsageStatus,
  type UseTurnkeyGasUsageOptions,
  useTurnkeyGasUsage,
} from './auth/turnkey/use-turnkey-gas-usage.js';
// Web-only Turnkey signing hooks — relocated from the frontend (auth group E).
export { useTurnkeySigningDeps } from './auth/turnkey/use-turnkey-signing.js';
export {
  type TurnkeySubmissionLogger,
  useTurnkeySubmissionDeps,
} from './auth/turnkey/use-turnkey-submission-deps.js';
// Transaction submission engine — relocated from frontend/src/lib/tx-engine/**
// (auth group E — `useSubmitTransaction`). The engine is platform-neutral in
// src/auth/tx-engine/; this hook is the web entry point.
export {
  type SubmitTransactionFn,
  type UseSubmitTransactionDeps,
  useSubmitTransaction,
} from './auth/tx/use-submit-transaction.js';
// Self-contained web wallets provider — replaces the frontend WalletsBridge.
export {
  ARCHIVED_WALLETS_QUERY_KEY,
  fetchWallets,
  invalidateWalletCache,
  SdkWalletsProvider,
  useInvalidateWalletCache,
  WALLETS_QUERY_KEY,
} from './auth/wallets/use-sdk-wallets.js';
export {
  type CreateWebPlatformAdapterDeps,
  createWebPlatformAdapter,
} from './create-platform-adapter.js';
export { createWebDefaultAdapter } from './default-adapter.js';
// Gifting host context — frontend mounts <GiftingHostProvider> to supply
// the concrete chain-submit hook implementations. See
// `src/platform/web/gifts/host-context.tsx` for the delegation rationale.
export {
  type GiftingHostBindings,
  GiftingHostProvider,
  type GiftingHostProviderProps,
  useGiftingHostContext,
} from './gifts/host-context.js';
export { useClaimGift } from './gifts/use-claim-gift.js';
export { useSendOutcomeTokenGift } from './gifts/use-send-outcome-token-gift.js';
export { useSendUsdcGift } from './gifts/use-send-usdc-gift.js';
export { useWithdrawGift } from './gifts/use-withdraw-gift.js';
export { installWebVisibility } from './install-visibility.js';
// Web-only persister helper — backs the TanStack cache with
// `window.localStorage`. Consumers who want full control over the
// persister build their own; this is a zero-config shortcut.
export { createWebLocalStoragePersister } from './persister-helper.js';
export {
  type CheckedSellPositionAccountingInput,
  computeCheckedSellPositionAccounting,
} from './trading/helpers/pnl.js';
export {
  TradingHostProvider,
  type TradingHostProviderProps,
  useTradingHostContext,
} from './trading/host-context.js';
export type {
  BoostTradeParams,
  BuyTradeParams,
  MutationContext as TradeMutationContext,
  PositionAccountingDto,
  SellTradeParams,
  ShareTradeRequest,
  ShowToastInput as TradeShowToastInput,
  SwapTradeParams,
  ThesisTradeContext,
  TradeContext,
  TradeLogger,
  TradeMutationDeps,
  TradeShareContext,
  TxMessageKey,
  TxSubmissionStoreApi,
  TxTranslator,
} from './trading/types.js';
export { TRADING_TOAST_IDS, TX_MESSAGE_KEY_PATHS, TX_MESSAGE_KEYS } from './trading/types.js';
export { type UseBoostTradeResult, useBoostTrade } from './trading/use-boost-trade.js';
export { type UseBuyTradeResult, useBuyTrade } from './trading/use-buy-trade.js';
export {
  type UseClaimBackerFeesResult,
  useClaimBackerFees,
} from './trading/use-claim-backer-fees.js';
export { type UseClaimPrizeResult, useClaimPrize } from './trading/use-claim-prize.js';
export {
  CLAIM_RECEIPT_QUERY_KEY,
  type UseClaimReceiptResult,
  useClaimReceipt,
} from './trading/use-claim-receipt.js';
export { type UseSellTradeResult, useSellTrade } from './trading/use-sell-trade.js';
export { type UseSwapTradeResult, useSwapTrade } from './trading/use-swap-trade.js';
export { createBrowserVisibilityController } from './visibility-controller.js';
